import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { gallerySrc } from "@/data/galleryAssets";
import manifest from "@/data/galleryManifest.json";
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  categoryFor,
  type GalleryCategory,
} from "@/data/galleryCategories";

export const Route = createFileRoute("/gallery-admin")({
  component: GalleryAdminPage,
  head: () => ({
    meta: [
      { title: "Gallery Review — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

const FILES: string[] = manifest as string[];
const STORAGE_KEY = "rkd-gallery-admin-v1";

type State = {
  hidden: Record<string, boolean>;
  category: Record<string, GalleryCategory>;
};

function loadState(): State {
  if (typeof window === "undefined") return { hidden: {}, category: {} };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { hidden: {}, category: {} };
    const parsed = JSON.parse(raw);
    return {
      hidden: parsed.hidden ?? {},
      category: parsed.category ?? {},
    };
  } catch {
    return { hidden: {}, category: {} };
  }
}

function GalleryAdminPage() {
  const [state, setState] = useState<State>({ hidden: {}, category: {} });
  const [filter, setFilter] = useState<"all" | "kept" | "hidden">("all");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setState(loadState());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const toggleHide = (f: string) =>
    setState((s) => ({ ...s, hidden: { ...s.hidden, [f]: !s.hidden[f] } }));

  const setCat = (f: string, c: GalleryCategory) =>
    setState((s) => ({ ...s, category: { ...s.category, [f]: c } }));

  const hiddenCount = useMemo(
    () => Object.values(state.hidden).filter(Boolean).length,
    [state],
  );
  const keptCount = FILES.length - hiddenCount;

  const visible = useMemo(() => {
    return FILES.filter((f) => {
      if (filter === "hidden") return !!state.hidden[f];
      if (filter === "kept") return !state.hidden[f];
      return true;
    });
  }, [filter, state.hidden]);

  const copyJSON = async () => {
    const hidden = FILES.filter((f) => state.hidden[f]);
    const categoryOverrides: Record<string, GalleryCategory> = {};
    for (const [f, c] of Object.entries(state.category)) {
      if (c !== categoryFor(f)) categoryOverrides[f] = c;
    }
    const payload = { hidden, categoryOverrides };
    const text = JSON.stringify(payload, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy the JSON below:", text);
    }
  };

  const reset = () => {
    if (confirm("Reset all review decisions?")) {
      setState({ hidden: {}, category: {} });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-4 flex flex-wrap items-center gap-3">
          <div>
            <h1 className="font-display text-xl md:text-2xl">Gallery Review</h1>
            <p className="text-xs md:text-sm text-muted-foreground">
              {FILES.length} total · <span className="text-primary">{keptCount} keep</span> · <span className="text-destructive">{hiddenCount} hide</span>
            </p>
          </div>
          <div className="ml-auto flex flex-wrap gap-2 items-center">
            {(["all", "kept", "hidden"] as const).map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setFilter(k)}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                  filter === k
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border hover:border-primary"
                }`}
              >
                {k}
              </button>
            ))}
            <button
              type="button"
              onClick={reset}
              className="px-3 py-1.5 text-xs uppercase tracking-widest border border-border hover:border-destructive hover:text-destructive"
            >
              Reset
            </button>
            <button
              type="button"
              onClick={copyJSON}
              className="px-4 py-1.5 text-xs uppercase tracking-widest bg-primary text-primary-foreground border border-primary hover:opacity-90"
            >
              {copied ? "Copied ✓" : "Copy JSON"}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6">
        <p className="text-sm text-muted-foreground mb-6">
          Click <strong>Hide</strong> on any photo you don't want in the public gallery. Optionally change its category. Progress auto-saves in this browser. When done, click <strong>Copy JSON</strong> and paste it back to me.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {visible.map((f) => {
            const isHidden = !!state.hidden[f];
            const cat = state.category[f] ?? categoryFor(f);
            return (
              <div
                key={f}
                className={`relative border overflow-hidden bg-cream transition-opacity ${
                  isHidden ? "opacity-40 border-destructive" : "border-border"
                }`}
              >
                <div className="aspect-square relative">
                  <img
                    src={gallerySrc(f)}
                    alt={f}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute top-1 left-1 bg-black/70 text-white text-[10px] px-1.5 py-0.5 font-mono">
                    {f}
                  </div>
                </div>
                <div className="p-2 flex flex-col gap-2 bg-background">
                  <button
                    type="button"
                    onClick={() => toggleHide(f)}
                    className={`w-full py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                      isHidden
                        ? "bg-destructive text-destructive-foreground border-destructive"
                        : "bg-background border-border hover:border-destructive hover:text-destructive"
                    }`}
                  >
                    {isHidden ? "Hidden — Undo" : "Hide"}
                  </button>
                  <select
                    aria-label="Gallery category"
                    value={cat}
                    onChange={(e) => setCat(f, e.target.value as GalleryCategory)}
                    className="w-full text-xs py-1 px-1 border border-border bg-background"
                  >
                    {CATEGORY_ORDER.map((c) => (
                      <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
        {visible.length === 0 && (
          <p className="text-center text-muted-foreground py-16">
            No photos match this filter.
          </p>
        )}
      </div>
    </div>
  );
}